import { useCallback, useEffect, useRef, useState } from "react";
import { EonUiPreloaderSquares } from "@eon-ui/eon-ui-components-react";
import { useJwtToken } from "@me8eon/react_authentication";
import { createIncident } from "../../../services/incidentsApi";
import { api } from "../expertApi";
import type {
    AskContext,
    BatteryDevice,
    ItemDetail,
    TriageActionState,
    TriageSuccess,
} from "../aidwops.types";
import useAidwopsStore from "../AidwopsStore";
import useAidwopsTriageStore, { NO_SELECTION } from "../AidwopsTriageStore";
import {
    batteryBand,
    buildSwapTicket,
    formatDays,
    formatRuntime,
} from "./intuneTriage";
import { ChoiceButton, TriageToolbar } from "./TriageParts";
import "./ExpertQueue.scss";

type LoadState =
    | { status: "loading" }
    | { status: "error"; message: string }
    | { status: "ready"; devices: BatteryDevice[] };

/** The ServiceNow ticket the engineer asked for: being created, created (with its number), or refused. */
type SnowState =
    | { status: "idle" }
    | { status: "creating" }
    | {
          status: "created";
          number: string;
          count: number;
          ignored: number;
      }
    | { status: "error"; message: string };

interface BatteryDevicesSectionProps {
    item: ItemDetail;
    onActionChange: (action: TriageActionState | null) => void;
    onSuccess: (result: TriageSuccess) => void;
}

interface SwapActionData {
    itemId: string;
    swap: number;
    ignored: number;
    swapKeys: string[];
    ticketSummary: string;
    ticketDescription: string;
}

const SNOW_ASSIGNMENT_GROUP = "infosys-all-mwp-euc-deviceswap";
// Hardcoded while the ticket flow is being integrated; to come from the signed-in user later.
const SNOW_CONTACT_NUMBER = "+49 00000000000";

const keyOf = (device: BatteryDevice, index: number): string =>
    device.device_id ?? device.device_name ?? `device-${index}`;

/**
 * The ten devices with the least battery left, for a battery finding. They come from the pre-computed battery
 * index through `GET /api/intune/battery/worst-devices`, enriched with each device's serial number and warranty
 * expiry from the hardware-asset index: no live Intune call, and no user, location or watt-hour figures, because
 * neither index holds them.
 */
export function BatteryDevicesSection({
    item,
    onActionChange,
    onSuccess,
}: BatteryDevicesSectionProps) {
    const jwtToken = useJwtToken();

    const submitPrompt = useAidwopsStore((state) => state.submitPrompt);

    const selection =
        useAidwopsTriageStore((state) => state.selections[item.id]) ??
        NO_SELECTION;

    const toggle = useAidwopsTriageStore((state) => state.toggle);
    const setMany = useAidwopsTriageStore((state) => state.setMany);

    const [state, setState] = useState<LoadState>({
        status: "loading",
    });

    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const [attempt, setAttempt] = useState(0);

    const [snow, setSnow] = useState<SnowState>({
        status: "idle",
    });

    const [stagedKeys, setStagedKeys] = useState<Set<string>>(
        () => new Set<string>()
    );

    const rawDevices = state.status === "ready" ? state.devices : [];

    const visibleDevices = rawDevices
        .map((device, index) => ({
            device,
            key: keyOf(device, index),
        }))
        .filter(({ key }) => !stagedKeys.has(key));

    const devices = visibleDevices.map(({ device }) => device);
    const keys = visibleDevices.map(({ key }) => key);

    const swapEntries = visibleDevices.filter(
        ({ key }) => selection[key] === "swap"
    );

    const ignoredEntries = visibleDevices.filter(
        ({ key }) => selection[key] === "ignore"
    );

    const swapKeys = swapEntries.map(({ key }) => key);
    const swapDevices = swapEntries.map(({ device }) => device);
    const ignoredDevices = ignoredEntries.map(({ device }) => device);

    const swap = swapDevices.length;
    const ignored = ignoredDevices.length;

    const ticket = buildSwapTicket(item.id, swapDevices, ignoredDevices);

    const actionDataRef = useRef<SwapActionData>({
        itemId: item.id,
        swap: 0,
        ignored: 0,
        swapKeys: [],
        ticketSummary: "",
        ticketDescription: "",
    });

    const latestKeysRef = useRef<string[]>([]);
    const itemIdRef = useRef(item.id);
    const onSuccessRef = useRef(onSuccess);
    const jwtTokenRef = useRef(jwtToken);
    const isCreatingRef = useRef(false);

    actionDataRef.current = {
        itemId: item.id,
        swap,
        ignored,
        swapKeys,
        ticketSummary: ticket.summary,
        ticketDescription: ticket.description,
    };

    latestKeysRef.current = keys;
    itemIdRef.current = item.id;
    onSuccessRef.current = onSuccess;
    jwtTokenRef.current = jwtToken;

    useEffect(() => {
        setSnow((current) => {
            if (current.status === "creating") {
                return current;
            }

            return { status: "idle" };
        });
    }, [selection]);

    const getAccessToken = useCallback(async (): Promise<string> => {
        const accessToken = await jwtTokenRef.current();

        if (!accessToken) {
            throw new Error("Unable to acquire access token for expert API");
        }

        return accessToken;
    }, []);

    useEffect(() => {
        let isCurrent = true;

        setState({
            status: "loading",
        });

        void (async () => {
            try {
                const accessToken = await getAccessToken();

                const loadedDevices =
                    await api.getWorstBatteryDevices(accessToken);

                if (isCurrent) {
                    setState({
                        status: "ready",
                        devices: loadedDevices,
                    });
                }
            } catch (error) {
                if (isCurrent) {
                    setState({
                        status: "error",
                        message:
                            error instanceof Error
                                ? error.message
                                : String(error),
                    });
                }
            }
        })();

        return () => {
            isCurrent = false;
        };
    }, [attempt, getAccessToken]);

    const stageSnowSwap = useCallback(async (): Promise<void> => {
        if (isCreatingRef.current) {
            return;
        }

        const {
            itemId,
            swap: currentSwap,
            ignored: currentIgnored,
            swapKeys: currentSwapKeys,
            ticketSummary,
            ticketDescription,
        } = actionDataRef.current;

        if (currentSwap === 0) {
            return;
        }

        isCreatingRef.current = true;

        setSnow({
            status: "creating",
        });

        try {
            const created = await createIncident(
                {
                    contact_number: SNOW_CONTACT_NUMBER,
                    service: "",
                    short_description: ticketSummary,
                    description: ticketDescription,
                    external_ticket_id: itemId,
                    category: "User Service Request",
                    sub_category: "Degraded/Service",
                    contact_type: "Self-Service",
                    location_id: "",
                    assignment_group: SNOW_ASSIGNMENT_GROUP,
                },
                jwtTokenRef.current
            );

            if (!created?.number) {
                throw new Error("ServiceNow answered without a ticket number.");
            }

            const createdTicket: ItemDetail["tickets"][number] = {
                key: created.number,
                url: "/incidents",
                status: "created",
                system: "servicenow",
            };

            setSnow({
                status: "created",
                number: created.number,
                count: currentSwap,
                ignored: currentIgnored,
            });

            setStagedKeys(
                (current) => new Set<string>([...current, ...currentSwapKeys])
            );

            onSuccessRef.current({
                title: "ServiceNow swap request created",
                message: `Ticket ${
                    created.number
                } was created for ${currentSwap} ${
                    currentSwap === 1 ? "device" : "devices"
                }. ${currentIgnored} ignored.`,
                tickets: [createdTicket],
            });
        } catch (error) {
            setSnow({
                status: "error",
                message: error instanceof Error ? error.message : String(error),
            });

            throw error;
        } finally {
            isCreatingRef.current = false;
        }
    }, []);

    useEffect(() => {
        onActionChange({
            label: "Stage SNOW Swap",
            count: swap,
            isSubmitting: snow.status === "creating",
            disabled:
                state.status !== "ready" ||
                swap === 0 ||
                snow.status === "creating" ||
                snow.status === "created",
            execute: stageSnowSwap,
        });
    }, [onActionChange, snow.status, stageSnowSwap, state.status, swap]);

    /*
     * Clear the parent footer action and all saved swap/ignore
     * selections when this component unmounts.
     *
     * This does not depend on item.id changing.
     */
    useEffect(() => {
        return () => {
            onActionChange(null);

            setMany(itemIdRef.current, latestKeysRef.current, null);
        };
    }, [onActionChange, setMany]);

    const askAbout = (device: BatteryDevice) => {
        const prompt = device.device_name
            ? `Show battery health for device ${device.device_name}`
            : `Show battery details for Intune device ID ${device.device_id}`;

        const context: AskContext = {
            view: "queue",
            selected: item.id,
        };

        void submitPrompt(prompt, getAccessToken, context);
    };

    const newest = rawDevices
        .map((device) => device.created_at)
        .filter((createdAt): createdAt is string => Boolean(createdAt))
        .sort()
        .pop();

    const label = (
        <div className="expert-queue-item-detail-card-section-label">
            Top 10 worst devices by battery health
        </div>
    );

    if (state.status === "loading") {
        return (
            <div className="expert-queue-item-detail-card-section">
                {label}

                <div className="triage-loading">
                    <EonUiPreloaderSquares />

                    <p className="expert-queue-item-detail-card-section-text">
                        Loading the devices...
                    </p>
                </div>
            </div>
        );
    }

    if (state.status === "error") {
        return (
            <div className="expert-queue-item-detail-card-section">
                {label}

                <div className="ac-alert" role="alert">
                    <p className="ac-alert-title">Couldn't load the devices.</p>

                    <p className="ac-alert-detail">{state.message}</p>
                </div>

                <div>
                    <button
                        type="button"
                        className="triage-button"
                        onClick={() => setAttempt((current) => current + 1)}
                    >
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    if (rawDevices.length === 0) {
        return (
            <div className="expert-queue-item-detail-card-section">
                {label}

                <p className="expert-queue-item-detail-card-section-text">
                    The battery index lists no device that needs attention.
                </p>
            </div>
        );
    }

    return (
        <div className="expert-queue-item-detail-card-section">
            {label}

            <p className="expert-queue-item-detail-card-section-text">
                Lowest remaining capacity first, from the battery index
                {newest
                    ? `, record written ${new Date(newest).toLocaleString()}`
                    : ""}
                . It has no user or location.
            </p>

            <TriageToolbar
                counts={[
                    {
                        label: "swap",
                        value: swap,
                    },
                    {
                        label: "ignored",
                        value: ignored,
                    },
                    {
                        label: "not reviewed",
                        value: keys.length - swap - ignored,
                    },
                ]}
                actions={[
                    {
                        label: "Swap all",
                        onClick: () => setMany(item.id, keys, "swap"),
                    },
                    {
                        label: "Ignore all",
                        onClick: () => setMany(item.id, keys, "ignore"),
                    },
                    {
                        label: "Reset",
                        onClick: () => setMany(item.id, keys, null),
                    },
                ]}
            />

            {devices.length > 0 ? (
                <ul className="triage-list">
                    {devices.map((device, index) => {
                        const key = keys[index];
                        const choice = selection[key];

                        const capacity = device.max_capacity_percentage;

                        const band = batteryBand(capacity);

                        const name =
                            device.device_name ??
                            device.device_id ??
                            "Unknown device";

                        return (
                            <li
                                key={key}
                                className="triage-row"
                                data-choice={choice ?? ""}
                            >
                                <div className="triage-row-top">
                                    <span className="triage-rank">
                                        {index + 1}
                                    </span>

                                    <div className="triage-name">
                                        <strong>{name}</strong>

                                        {device.model && (
                                            <span className="triage-sub">
                                                {device.model}
                                            </span>
                                        )}

                                        {(device.serial_number ||
                                            device.warranty_expiration) && (
                                            <span className="triage-sub">
                                                Serial:{" "}
                                                <strong>
                                                    {device.serial_number ??
                                                        "n/a"}
                                                </strong>
                                                {" · "}
                                                Warranty:{" "}
                                                <strong>
                                                    {device.warranty_expiration
                                                        ? new Date(
                                                              device.warranty_expiration
                                                          ).toLocaleDateString(
                                                              undefined,
                                                              {
                                                                  year: "numeric",
                                                                  month: "short",
                                                                  day: "numeric",
                                                              }
                                                          )
                                                        : "n/a"}
                                                </strong>
                                            </span>
                                        )}
                                    </div>

                                    <span
                                        className={`triage-health triage-health--${band}`}
                                        tabIndex={0}
                                    >
                                        {capacity === null
                                            ? "n/a"
                                            : `${capacity}%`}

                                        {capacity !== null && (
                                            <span className="triage-health-bar">
                                                <i
                                                    style={{
                                                        width: `${Math.min(
                                                            100,
                                                            Math.max(
                                                                0,
                                                                capacity
                                                            )
                                                        )}%`,
                                                    }}
                                                />
                                            </span>
                                        )}

                                        <span
                                            className="triage-health-pop"
                                            role="tooltip"
                                        >
                                            <span className="triage-health-pop-head">
                                                <strong>
                                                    {name} ·{" "}
                                                    {capacity === null
                                                        ? "no capacity in the index"
                                                        : `${capacity}% health`}
                                                </strong>

                                                <span
                                                    className={`triage-health-pop-chip triage-health-pop-chip--${band}`}
                                                >
                                                    {band === "critical"
                                                        ? "Critical"
                                                        : band === "degraded"
                                                          ? "Degraded"
                                                          : "Unknown"}
                                                </span>
                                            </span>

                                            <span className="triage-health-pop-grid">
                                                <span>
                                                    <small>Max capacity</small>
                                                    <b>
                                                        {capacity === null
                                                            ? "n/a"
                                                            : `${capacity}% of design`}
                                                    </b>
                                                </span>

                                                <span>
                                                    <small>Est. runtime</small>
                                                    <b>
                                                        {formatRuntime(
                                                            device.estimated_runtime_minutes
                                                        )}
                                                    </b>
                                                </span>

                                                <span>
                                                    <small>Battery age</small>
                                                    <b>
                                                        {formatDays(
                                                            device.battery_age_days
                                                        )}
                                                    </b>
                                                </span>

                                                <span>
                                                    <small>Full drains</small>
                                                    <b>
                                                        {device.full_battery_drain_count ??
                                                            "n/a"}
                                                    </b>
                                                </span>
                                            </span>

                                            <span className="triage-health-pop-foot">
                                                {device.health_status ??
                                                    "No status in the index"}
                                            </span>
                                        </span>
                                    </span>
                                </div>

                                {expanded[key] && (
                                    <dl className="triage-details">
                                        <div>
                                            <dt>Status</dt>
                                            <dd>
                                                {device.health_status ?? "n/a"}
                                            </dd>
                                        </div>

                                        <div>
                                            <dt>Estimated runtime</dt>
                                            <dd>
                                                {formatRuntime(
                                                    device.estimated_runtime_minutes
                                                )}
                                            </dd>
                                        </div>

                                        <div>
                                            <dt>Battery age</dt>
                                            <dd>
                                                {formatDays(
                                                    device.battery_age_days
                                                )}
                                            </dd>
                                        </div>

                                        <div>
                                            <dt>Full drains</dt>
                                            <dd>
                                                {device.full_battery_drain_count ??
                                                    "n/a"}
                                            </dd>
                                        </div>

                                        <div>
                                            <dt>Device ID</dt>
                                            <dd>{device.device_id ?? "n/a"}</dd>
                                        </div>

                                        <div>
                                            <dt>Serial number</dt>
                                            <dd>
                                                {device.serial_number ?? "n/a"}
                                            </dd>
                                        </div>

                                        <div>
                                            <dt>Warranty expiry</dt>
                                            <dd>
                                                {device.warranty_expiration
                                                    ? new Date(
                                                          device.warranty_expiration
                                                      ).toLocaleDateString()
                                                    : "n/a"}
                                            </dd>
                                        </div>

                                        <div>
                                            <dt>Record written</dt>
                                            <dd>
                                                {device.created_at
                                                    ? new Date(
                                                          device.created_at
                                                      ).toLocaleString()
                                                    : "n/a"}
                                            </dd>
                                        </div>
                                    </dl>
                                )}

                                <div className="triage-actions">
                                    <ChoiceButton
                                        label="Swap request"
                                        tone="act"
                                        pressed={choice === "swap"}
                                        onClick={() =>
                                            toggle(item.id, key, "swap")
                                        }
                                    />

                                    <ChoiceButton
                                        label="Ignore"
                                        tone="ignore"
                                        pressed={choice === "ignore"}
                                        onClick={() =>
                                            toggle(item.id, key, "ignore")
                                        }
                                    />

                                    <button
                                        type="button"
                                        className="triage-link"
                                        aria-expanded={Boolean(expanded[key])}
                                        onClick={() =>
                                            setExpanded((current) => ({
                                                ...current,
                                                key: !current[key],
                                            }))
                                        }
                                    >
                                        {expanded[key]
                                            ? "Hide details"
                                            : "Details"}
                                    </button>

                                    <button
                                        type="button"
                                        className="triage-link triage-link--end"
                                        disabled={
                                            !device.device_name &&
                                            !device.device_id
                                        }
                                        onClick={() => askAbout(device)}
                                    >
                                        Ask AI
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <p className="expert-queue-item-detail-card-section-text">
                    All flagged devices have been staged for a swap.
                </p>
            )}

            <details className="triage-preview">
                <summary>Jira / SNOW note preview</summary>

                <div className="triage-preview-body">
                    <strong>{ticket.summary}</strong>
                    <pre>{ticket.description}</pre>
                </div>
            </details>

            {snow.status === "creating" && (
                <div className="triage-snow-result" role="status">
                    Creating the ServiceNow ticket...
                </div>
            )}

            {snow.status === "error" && (
                <div className="ac-alert" role="alert">
                    <p className="ac-alert-title">
                        Couldn't create the ServiceNow ticket.
                    </p>

                    <p className="ac-alert-detail">{snow.message}</p>
                </div>
            )}
        </div>
    );
}

export default BatteryDevicesSection;
